import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Producto } from "../models/ProductoModel";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { isAbortError } from "../services/fetchService";
import { useUserContext } from "./UserContext";
import { useRealtime } from "./RealtimeContext";

export type ProductosVentaActualizados = {
  idLocales?: number[];
  motivo?: "precio_venta" | "para_vender" | "productos_compra";
  operationId?: string;
  fechaUtc?: string;
};

type Entry = { productos: Producto[]; loading: boolean; error?: Error; revision: number; initialized: boolean };
type ContextType = {
  entries: Record<string, Entry>;
  acquire: (idLocal: string) => () => void;
  refresh: (idLocal: string) => Promise<void>;
};

const Context = createContext<ContextType | undefined>(undefined);
const emptyEntry: Entry = { productos: [], loading: false, revision: 0, initialized: false };

export const getProductosVentaRevalidationSeconds = () => {
  const fallback = process.env.NODE_ENV === "production" ? 300 : 60;
  const configured = Number(process.env.REACT_APP_PRODUCTOS_VENTA_REVALIDACION_SEGUNDOS);
  return Number.isFinite(configured) && configured > 0 ? configured : fallback;
};

export const ProductosVentaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useUserContext();
  const { subscribe } = useRealtime();
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const activeRef = useRef<Map<string, number>>(new Map());
  const pendingRef = useRef<Map<string, Promise<void>>>(new Map());
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  const previousTokenRef = useRef(user?.token);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const refresh = useCallback(async (idLocal: string) => {
    if (!idLocal || !user) return;
    const pending = pendingRef.current.get(idLocal);
    if (pending) return pending;

    const controller = new AbortController();
    controllersRef.current.set(idLocal, controller);
    setEntries((current) => ({ ...current, [idLocal]: { ...(current[idLocal] ?? emptyEntry), loading: true, error: undefined } }));
    const task = fetchGetProductosParaVenta(idLocal, user, controller.signal)
      .then((result: Producto[] | string) => {
        const productos = typeof result === "string" ? JSON.parse(result) : result;
        setEntries((current) => {
          const previous = current[idLocal] ?? emptyEntry;
          const changed = previous.initialized && JSON.stringify(previous.productos) !== JSON.stringify(productos);
          return { ...current, [idLocal]: { productos, loading: false, initialized: true, revision: previous.revision + (changed ? 1 : 0) } };
        });
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        setEntries((current) => ({ ...current, [idLocal]: { ...(current[idLocal] ?? emptyEntry), loading: false, error } }));
      })
      .finally(() => {
        pendingRef.current.delete(idLocal);
        controllersRef.current.delete(idLocal);
      });
    pendingRef.current.set(idLocal, task);
    return task;
  }, [user]);

  const acquire = useCallback((idLocal: string) => {
    activeRef.current.set(idLocal, (activeRef.current.get(idLocal) ?? 0) + 1);
    if (!entriesRef.current[idLocal]?.initialized) void refresh(idLocal);
    return () => {
      const count = (activeRef.current.get(idLocal) ?? 1) - 1;
      if (count <= 0) activeRef.current.delete(idLocal); else activeRef.current.set(idLocal, count);
    };
  }, [refresh]);

  useEffect(() => {
    const interval = window.setInterval(() => activeRef.current.forEach((_, idLocal) => void refresh(idLocal)), getProductosVentaRevalidationSeconds() * 1000);
    return () => window.clearInterval(interval);
  }, [refresh]);

  useEffect(() => subscribe("ProductosVentaActualizados", (event: ProductosVentaActualizados) => {
    if (event?.motivo === "productos_compra") return;
    const affected = event?.idLocales ?? [];
    activeRef.current.forEach((_, idLocal) => {
      if (affected.length === 0 || affected.includes(Number(idLocal))) void refresh(idLocal);
    });
  }), [refresh, subscribe]);

  useEffect(() => () => controllersRef.current.forEach((controller) => controller.abort()), []);
  useEffect(() => {
    if (previousTokenRef.current === user?.token) return;
    previousTokenRef.current = user?.token;
    controllersRef.current.forEach((controller) => controller.abort());
    pendingRef.current.clear();
    setEntries({});
    activeRef.current.forEach((_, idLocal) => void refresh(idLocal));
  }, [refresh, user?.token]);

  const value = useMemo(() => ({ entries, acquire, refresh }), [acquire, entries, refresh]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useProductosVenta = (idLocal?: string) => {
  const context = useContext(Context);
  if (!context) throw new Error("useProductosVenta must be used within ProductosVentaProvider");
  const { acquire, entries, refresh } = context;
  useEffect(() => idLocal ? acquire(idLocal) : undefined, [acquire, idLocal]);
  const entry = idLocal ? entries[idLocal] ?? emptyEntry : emptyEntry;
  return { ...entry, refresh: () => idLocal ? refresh(idLocal) : Promise.resolve() };
};
